import type { SetStatus } from '@lift2fit/shared-types';
import { cn } from '@/lib/format';

const GLYPH: Record<SetStatus, string> = {
  pending: '·',
  done: '✓',
  pr: '▲',
  below: '▼',
  failed: '✕',
};

const LABEL: Record<SetStatus, string> = {
  pending: 'Open',
  done: 'Done',
  pr: 'PR',
  below: 'Below target',
  failed: 'Failed',
};

const TONE: Record<SetStatus, string> = {
  pending: 'border-hairline text-ink-muted',
  done: 'border-success/60 bg-success/10 text-success',
  pr: 'border-accent bg-accent/15 text-accent',
  below: 'border-warning/60 bg-warning/10 text-warning',
  failed: 'border-danger/60 bg-danger/10 text-danger',
};

/**
 * Status is never carried by colour alone (UI/UX spec §7): a glyph, the word,
 * and the colour all move together.
 */
export function StatusTag({ status, className }: { status: SetStatus; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill border px-2 py-[2px] text-[10px] font-bold uppercase leading-none tracking-[0.08em]',
        TONE[status],
        className,
      )}
    >
      <span aria-hidden="true">{GLYPH[status]}</span>
      {LABEL[status]}
    </span>
  );
}

/** Screen-reader helper: the full sentence for a set row. */
export function setAriaLabel(args: {
  exercise: string;
  setNumber: number;
  weightKg: number;
  reps: number;
  unit: 'kg' | 'lb';
  displayWeight: number;
  rpe: number | null;
  status: SetStatus;
}): string {
  const { exercise, setNumber, reps, unit, displayWeight, rpe, status } = args;
  return `${exercise}, set ${setNumber}, ${displayWeight} ${unit}, ${reps} reps${
    rpe === null ? '' : `, RPE ${rpe}`
  }, ${LABEL[status]}`;
}
